import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Icons
import { CiCalendar, CiSearch } from "react-icons/ci";
import { IoCartOutline } from "react-icons/io5";
import { SlLocationPin } from "react-icons/sl";

// Components
import DashboardLayout from "../DashboardHome/DashboardLayout";
import EventsTab from "./Events";

// Tab configuration
const TAB_CONFIG = [
  {
    id: 1,
    label: "Events",
    icon: CiCalendar,
    width: "w-[70px] md:w-[83px]",
  },
  {
    id: 2,
    label: "Businesses",
    icon: IoCartOutline,
    width: "w-[111px]",
  },
  {
    id: 3,
    label: "Things to do (Soon)",
    icon: SlLocationPin,
    width: "w-[168px]",
  },
];

function Explore() {
  // Redux state
  const darktheme = useSelector((state) => state.features.settings.darktheme);
  const token = useSelector((state) => state.auth.accessToken);
  
  // Hooks
  const navigate = useNavigate();
  
  // Component state
  const [activeTab, setActiveTab] = useState(1);

  // Authentication check on mount
  useEffect(() => {
    if (!token) {
      navigate("/dashboard/home");
    }
  }, [token, navigate]);

  // Theme-based CSS classes helper
  const getThemeClasses = (lightClass, darkClass) => 
    darktheme ? darkClass : lightClass;

  // Tab button styling helper
  const getTabButtonClasses = (tabId) => {
    const isActive = activeTab === tabId;
    const tabConfig = TAB_CONFIG.find(tab => tab.id === tabId);
    return `${
      isActive
        ? `${darktheme ? `bg-[#F2F2F5] text-[#1C2024]` : `bg-[#20232D] text-white`}`
        : `${darktheme ? `bg-[#20232D] text-white` : `bg-[#F2F2F5] text-[#7E808A]`}`
    } ${tabConfig.width} h-[36px] px-2 py-1.5 md:px-[10px] md:py-[8px] gap-[4px] flex items-center rounded-[999px] text-xs md:text-[14px]`;
  };

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 1:
        return <EventsTab />;
      case 2:
        // Placeholder for Businesses tab
        return <div>Businesses content coming soon...</div>;
      case 3:
        // Placeholder for Things to do tab
        return <div>Things to do content coming soon...</div>;
      default:
        return <EventsTab />;
    }
  };

  return (
    <DashboardLayout>
      <div className={`w-full min-h-screen ${getThemeClasses("bg-white", "bg-kdark3")}`}>
        {/* Search Bar */}
        <div className={`flex items-center mx-4 lg:w-[540px] mt-6 lg:mx-auto h-[36px] gap-[10px] ${getThemeClasses("bg-[#F2F2F5]", "bg-[black] border-[#fafafa]")} rounded-[31px] border-[1px] border-[#02023414] py-[8px] px-[12px]`}>
          <CiSearch className="text-[#60646C] w-[16px] h-[16px]" />
          <input
            placeholder="Search for Events"
            className="w-full h-full bg-transparent text-[#828282] text-[14px] border-none outline-none"
          />
        </div>

        {/* Main Content Container */}
        <div className="max-w-[850px] 2xl:max-w-[1200px] mt-6 lg:mt-[32px] mx-4 lg:mx-0 lg:ml-[125px] pb-6 2xl:ml-[180px]">
          {/* Header Section */}
          <div className="mb-2 w-[375px] h-[83px] flex-col gap-[15px]">
            <h4 className={`text-xl md:text-[24px] font-[700] ${darktheme ? "text-[F2F2F5]" : "text-[#1c2024]"}`}>
              Explore everything near you
            </h4>
            
            {/* Tab Navigation */}
            <div className="flex gap-2 mt-4 items-center">
              {TAB_CONFIG.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={getTabButtonClasses(tab.id)}
                    aria-pressed={activeTab === tab.id}
                  >
                    <IconComponent className="w-[11.6px] h-[12px]" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div className="my-[20px] border-[#E2E4E9] border-[.1px]" />

          {/* Tab Content */}
          <div>
            {renderTabContent()}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default Explore;